import { useState } from 'react';
import { Settings, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Modal } from '../../shared/components/Modal';
import { formatBRL, formatDateTime } from '../../shared/lib/format';
import { useAuthStore } from '../../store/authStore';
import {
  useAbrirComanda,
  useComandas,
  useConfigComandas,
  useSetConfigComandas,
  useComandaItens,
} from './hooks';
import { usePdvStore } from '../pdv/pdvStore';
import type { Comanda } from './types';

export function ComandasPage() {
  const navigate = useNavigate();
  const setPdvComandaId = usePdvStore((s) => s.setComandaId);
  const user = useAuthStore((s) => s.user);
  
  const { data: config, isLoading: loadingConfig } = useConfigComandas();
  const { data: comandasAbertas, isLoading: loadingComandas } = useComandas({ status: 'aberta' });
  const abrirMut = useAbrirComanda();

  const [configOpen, setConfigOpen] = useState(false);
  const [viewComanda, setViewComanda] = useState<Comanda | null>(null);

  const totalComandas = config?.total_comandas || 100;
  const isLoading = loadingConfig || loadingComandas;

  const abertasByNumero = new Map<number, Comanda>();
  if (comandasAbertas) {
    comandasAbertas.forEach((c) => abertasByNumero.set(c.numero, c));
  }

  const handleComandaClick = async (numero: number) => {
    const aberta = abertasByNumero.get(numero);
    if (aberta) {
      setViewComanda(aberta);
    } else {
      // Criar nova comanda
      try {
        const nova = await abrirMut.mutateAsync({ numero });
        setPdvComandaId(nova.id);
        navigate('/pdv');
      } catch (err: any) {
        // erro ja tratado no hook
      }
    }
  };

  const isManager = user?.role === 'admin' || user?.role === 'gerente';

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Comandas</h1>
          <p className="mt-1 text-sm text-slate-500">
            Grade visual de comandas. Clique em uma vazia para iniciar o atendimento.
          </p>
        </div>
        {isManager && (
          <button className="btn-secondary" onClick={() => setConfigOpen(true)}>
            <Settings className="h-4 w-4" /> Configurar Grade
          </button>
        )}
      </header>

      {isLoading ? (
        <div className="flex justify-center p-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12">
          {Array.from({ length: totalComandas }, (_, i) => i + 1).map((numero) => {
            const aberta = abertasByNumero.get(numero);
            return (
              <button
                key={numero}
                disabled={abrirMut.isPending}
                onClick={() => handleComandaClick(numero)}
                className={`flex aspect-square flex-col items-center justify-center rounded-xl border-2 transition-all active:scale-95 ${
                  aberta
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 shadow-sm'
                    : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <span className="text-xl font-bold">{numero}</span>
              </button>
            );
          })}
        </div>
      )}

      <ConfigModal open={configOpen} onClose={() => setConfigOpen(false)} total={totalComandas} />
      {viewComanda && (
        <ComandaResumoModal
          comanda={viewComanda}
          onClose={() => setViewComanda(null)}
          onAddMais={() => {
            setPdvComandaId(viewComanda.id);
            navigate('/pdv');
          }}
        />
      )}
    </div>
  );
}

function ConfigModal({ open, onClose, total }: { open: boolean; onClose: () => void; total: number }) {
  const [val, setVal] = useState(total.toString());
  const setMut = useSetConfigComandas();

  const handleSave = async () => {
    const num = Number(val);
    if (!Number.isInteger(num) || num <= 0 || num > 5000) {
      toast.error('Informe um numero valido entre 1 e 5000');
      return;
    }
    await setMut.mutateAsync({ total_comandas: num });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Configurar Grade de Comandas" size="sm">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Quantidade de Comandas</label>
          <input
            type="number"
            min={1}
            max={5000}
            className="input"
            value={val}
            onChange={(e) => setVal(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            Define o numero total de quadrados que serao desenhados na tela de comandas.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary flex-1" onClick={onClose} disabled={setMut.isPending}>
            Cancelar
          </button>
          <button className="btn-primary flex-1" onClick={handleSave} disabled={setMut.isPending}>
            {setMut.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ComandaResumoModal({
  comanda,
  onClose,
  onAddMais,
}: {
  comanda: Comanda;
  onClose: () => void;
  onAddMais: () => void;
}) {
  const { data, isLoading } = useComandaItens(comanda.id);

  return (
    <Modal open={true} onClose={onClose} title={`Comanda #${comanda.numero}`} size="md">
      {isLoading || !data ? (
        <div className="space-y-3">
          <div className="h-20 animate-pulse rounded-lg bg-slate-100" />
          <div className="h-40 animate-pulse rounded-lg bg-slate-100" />
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <div className="grid gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-2">
            <Field label="Aberta em" value={formatDateTime(comanda.aberta_em)} />
            <Field label="Status" value="Aberta" />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Itens Lançados ({data.itens.length})
            </h3>
            {data.itens.length === 0 ? (
              <p className="rounded-lg border border-slate-200 p-4 text-center text-slate-500">
                Nenhum item lançado ainda.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {data.itens.map((it) => (
                  <li key={it.id} className="p-3 hover:bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900">
                          {Number(it.quantidade).toLocaleString('pt-BR')}x {it.produto_nome}
                          {it.tamanho_nome ? ` (${it.tamanho_nome})` : ''}
                        </div>
                        {it.complementos.length > 0 && (
                          <div className="mt-0.5 text-xs text-slate-500">
                            + {it.complementos.map((c) => c.nome).join(', ')}
                          </div>
                        )}
                        {it.observacao && (
                          <div className="mt-0.5 text-xs italic text-slate-500">
                            Obs: {it.observacao}
                          </div>
                        )}
                      </div>
                      <div className="ml-4 text-right">
                        <div className="font-semibold text-slate-900">
                          {formatBRL(Number(it.subtotal))}
                        </div>
                        <div className="mt-1 text-[10px] text-slate-400">
                          Lançado às {new Date(it.criado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          por {it.operador?.nome ?? 'Operador'}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t border-slate-200">
            <button className="btn-secondary flex-1" onClick={onAddMais}>
              <Plus className="mr-1 h-4 w-4" /> Adicionar Mais
            </button>
            <button className="btn-primary flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={onAddMais}>
              Cobrar (ir pro PDV)
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 font-medium capitalize text-slate-900">{value}</div>
    </div>
  );
}
